/**
 * Club Onboarding Service
 * Manages the 7-step club setup wizard API calls.
 *
 * Endpoints (prefix: /onboarding):
 *   POST /start              — Create institution + onboarding
 *   GET  /state              — Full wizard state
 *   PUT  /step/:step         — Save step data
 *   GET  /clubs/search?q=    — Search swimming_clubs for linking
 *   POST /results-link       — Confirm club ↔ swimming_clubs link
 *   POST /contacts           — Save contacts (step 2)
 *   POST /sports             — Save sports structure (step 3)
 *   POST /services           — Save service interests (step 5)
 *   POST /invites            — Send user invitations (step 6)
 *   POST /complete           — Finalize onboarding (step 7)
 */
import { AZURE } from './api';

// ── Start onboarding ───────────────────────────────────────────────────────
export const startOnboarding = async ({ name, org_type, primary_sport }) => {
  const { data } = await AZURE.post('/onboarding/start', { name, org_type, primary_sport });
  return data;
};

// ── Full state ─────────────────────────────────────────────────────────────
export const getOnboardingState = async (institutionId) => {
  const params = institutionId ? { institution_id: institutionId } : {};
  const { data } = await AZURE.get('/onboarding/state', { params });
  return data;
};

// ── Save step (generic — works for step 1 institutional data) ──────────────
export const saveStep = async (step, payload) => {
  const { data } = await AZURE.put(`/onboarding/step/${step}`, payload);
  return data;
};

// ── Search swimming clubs for results linking (step 4) ─────────────────────
export const searchClubsForLinking = async (query) => {
  if (!query || query.length < 2) return [];
  const { data } = await AZURE.get('/onboarding/clubs/search', { params: { q: query } });
  return data?.data || [];
};

// ── Save results link (step 4) ─────────────────────────────────────────────
export const saveResultsLink = async (payload) => {
  const { data } = await AZURE.post('/onboarding/results-link', payload);
  return data;
};

// ── Save contacts (step 2) ─────────────────────────────────────────────────
export const saveContacts = async (institutionId, contacts) => {
  const { data } = await AZURE.post('/onboarding/contacts', {
    institution_id: institutionId,
    contacts,
  });
  return data;
};

// ── Save sports structure (step 3) ─────────────────────────────────────────
export const saveSportsStructure = async (institutionId, sports) => {
  const { data } = await AZURE.post('/onboarding/sports', {
    institution_id: institutionId,
    sports,
  });
  return data;
};

// ── Save service interests (step 5) ────────────────────────────────────────
export const saveServiceInterests = async (institutionId, services) => {
  const { data } = await AZURE.post('/onboarding/services', {
    institution_id: institutionId,
    services,
  });
  return data;
};

// ── Save invites (step 6) ──────────────────────────────────────────────────
export const saveInvites = async (institutionId, invites) => {
  const { data } = await AZURE.post('/onboarding/invites', {
    institution_id: institutionId,
    invites,
  });
  return data;
};

// ── Complete onboarding (step 7) ───────────────────────────────────────────
export const completeOnboarding = async (institutionId) => {
  const { data } = await AZURE.post('/onboarding/complete', {
    institution_id: institutionId,
  });
  return data;
};

// ── Discard onboarding (D1: descarte real) ────────────────────────────────
// Apaga transacionalmente o subtree do /start + steps no backend e libera o
// user do funil (onboarding_status -> 'none'). Institution ativada é bloqueada.
export const discardOnboarding = async () => {
  const { data } = await AZURE.post('/onboarding/discard', {});
  return data;
};

// ── Constants ──────────────────────────────────────────────────────────────

export const ORG_TYPES = [
  { value: 'club', label: 'Clube' },
  { value: 'academy', label: 'Academia / Escola' },
  { value: 'team', label: 'Equipe / Time' },
  { value: 'federation', label: 'Federação / Confederação' },
  { value: 'school', label: 'Projeto Social / Escola' },
  { value: 'other', label: 'Outro' },
];

// Gancho multi-sport: a lista completa fica como estrutura (futuro), mas no
// launch SÓ natação está habilitada. A UI filtra por `enabled` — não apagar
// as demais entradas, apenas destravar quando o esporte entrar no produto.
export const SPORT_OPTIONS = [
  { value: 'swimming', label: 'Natação', enabled: true },
  { value: 'football', label: 'Futebol', enabled: false },
  { value: 'water_polo', label: 'Polo Aquático', enabled: false },
  { value: 'athletics', label: 'Atletismo', enabled: false },
  { value: 'judo', label: 'Judô', enabled: false },
  { value: 'gymnastics', label: 'Ginástica', enabled: false },
  { value: 'volleyball', label: 'Vôlei', enabled: false },
  { value: 'basketball', label: 'Basquete', enabled: false },
  { value: 'other', label: 'Outro', enabled: false },
];

// Opções visíveis na UI (só habilitadas)
export const ENABLED_SPORTS = SPORT_OPTIONS.filter(s => s.enabled);

export const CATEGORY_OPTIONS = [
  { value: 'mirim', label: 'Mirim (até 10)' },
  { value: 'pre_mirim', label: 'Pré-mirim (11-12)' },
  { value: 'infantil', label: 'Infantil (13-14)' },
  { value: 'juvenil', label: 'Juvenil (15-16)' },
  { value: 'junior', label: 'Junior (17-18)' },
  { value: 'adulto', label: 'Adulto / Sênior' },
  { value: 'master', label: 'Master (25+)' },
];

export const CONTACT_ROLES = [
  { value: 'institutional', label: 'Responsável Institucional', desc: 'Presidente, Diretor' },
  { value: 'technical', label: 'Coordenador Técnico', desc: 'Head Coach, Dir. Técnico' },
  { value: 'performance', label: 'Preparação Física', desc: 'Prep. Físico principal' },
  { value: 'commercial', label: 'Comercial', desc: 'Marketing, Patrocínios' },
  { value: 'it', label: 'TI / Tecnologia', desc: 'Responsável por sistemas' },
  { value: 'admin_master', label: 'Admin Master VUKZ', desc: 'Primeiro admin do sistema' },
];

export const INVITE_ROLES = [
  { value: 'GESTOR', label: 'Gestor', desc: 'Acesso completo de administração' },
  { value: 'TREINADOR', label: 'Treinador', desc: 'Treinos, atletas, resultados' },
  { value: 'PREPARADOR_FISICO', label: 'Preparador Físico', desc: 'Wellness, métricas, treinos' },
  { value: 'ATLETA', label: 'Atleta', desc: 'Visualizar seus dados e treinos' },
  { value: 'FISIOTERAPEUTA', label: 'Fisioterapeuta', desc: 'Atendimentos, lesões' },
  { value: 'NUTRICIONISTA', label: 'Nutricionista', desc: 'Dietas, composição corporal' },
  { value: 'MEDICO', label: 'Médico', desc: 'Laudos, atestados' },
  { value: 'ANALISTA', label: 'Analista de Desempenho', desc: 'Vídeo, dados, relatórios' },
  { value: 'OBSERVADOR', label: 'Observador / Scout', desc: 'Apenas visualização' },
];

export const SERVICE_MODULES = [
  {
    group: 'Resultados & Rankings',
    services: [
      { code: 'results', label: 'Gestão de Resultados', desc: 'Importar e consultar resultados oficiais' },
      { code: 'rankings', label: 'Rankings', desc: 'Rankings internos e comparativos' },
      { code: 'evolution', label: 'Evolução Individual', desc: 'Gráficos de evolução por atleta' },
      { code: 'benchmarking', label: 'Benchmarking', desc: 'Comparação com atletas externos' },
    ],
  },
  {
    group: 'Dashboards & Análises',
    services: [
      { code: 'dashboard_tech', label: 'Dashboard Técnico', desc: 'Visão geral para treinador' },
      { code: 'dashboard_phys', label: 'Dashboard Preparação', desc: 'Visão geral para prep. físico' },
      { code: 'comparatives', label: 'Comparativos', desc: 'Análises comparativas entre atletas' },
      { code: 'reports', label: 'Relatórios', desc: 'PDFs e exports customizados' },
    ],
  },
  {
    group: 'Treinamento',
    services: [
      { code: 'builder_tech', label: 'Builder Técnico', desc: 'Montar treinos técnicos com IA' },
      { code: 'builder_phys', label: 'Builder Físico', desc: 'Periodização e cargas' },
      { code: 'execution_canvas', label: 'Execução ao Vivo', desc: 'Canvas de acompanhamento em tempo real' },
      { code: 'session_control', label: 'Controle de Sessão', desc: 'Presença, RPE, feedback' },
    ],
  },
  {
    group: 'Saúde & Performance',
    services: [
      { code: 'physio', label: 'Fisioterapia', desc: 'Controle de atendimentos e lesões' },
      { code: 'nutrition', label: 'Nutrição', desc: 'Planos alimentares, composição corporal' },
      { code: 'medicine', label: 'Medicina Esportiva', desc: 'Laudos, atestados, exames' },
      { code: 'injury_recovery', label: 'Reabilitação', desc: 'Protocolos de recuperação' },
    ],
  },
  {
    group: 'IA & Automação',
    services: [
      { code: 'ai_assistant', label: 'Assistente IA', desc: 'Chat inteligente com seus dados' },
      { code: 'ai_training', label: 'IA Treinamento', desc: 'Sugestões automáticas de treinos' },
      { code: 'ai_insights', label: 'IA Insights', desc: 'Alertas e recomendações preditivas' },
      { code: 'ai_alerts', label: 'IA Alertas', desc: 'Notificações inteligentes' },
    ],
  },
  {
    group: 'Vídeo & Sensores',
    services: [
      { code: 'video_upload', label: 'Upload de Vídeo', desc: 'Enviar vídeos de treinos/jogos' },
      { code: 'video_analysis', label: 'Análise de Vídeo', desc: 'IA para análise biomecânica' },
      { code: 'video_tagging', label: 'Video Tagging', desc: 'Marcar momentos-chave' },
      { code: 'video_multicam', label: 'Multicâmera', desc: 'Sincronização multi-ângulo' },
    ],
  },
  {
    group: 'Wearables & IoT',
    services: [
      { code: 'wearables', label: 'Wearables', desc: 'Integração com relógios e sensores' },
      { code: 'sensors_env', label: 'Sensores Ambientais', desc: 'Temperatura da piscina, umidade' },
      { code: 'sensor_integration', label: 'Integração', desc: 'APIs de sensores customizados' },
    ],
  },
];
